import React, { useState, useEffect } from 'react';
import { managerIntelAPI } from '../../utils/api';

// Status color system — shell palette only.
// ink = text/bar color, soft = chip background.
const STATUS_META = {
  startup: { label: 'New startup', short: 'Startup', ink: 'var(--ft-green-ink)', soft: 'var(--ft-green-soft)' },
  orphan: { label: 'Orphan adopted', short: 'Adopted', ink: 'var(--ft-brand-ink)', soft: 'var(--ft-brand-soft)' },
  established: { label: 'Continuing', short: '', ink: 'var(--ft-ink-3)', soft: 'var(--ft-surface)' },
  left: { label: 'Abandoned', short: 'Abandoned', ink: '#FF3B30', soft: 'rgba(255, 59, 48, 0.1)' },
  folded: { label: 'League folded', short: 'Folded', ink: 'var(--ft-ink-3)', soft: 'var(--ft-surface)' },
};

const YOY_CSS = `
@keyframes ft-yoy-pulse { 0%, 80%, 100% { opacity: .25; transform: scale(.8); } 40% { opacity: 1; transform: scale(1); } }
.ft-yoy-pulsedot { width: 6px; height: 6px; border-radius: 50%; background: var(--ft-ink-3); animation: ft-yoy-pulse 1.2s ease-in-out infinite; }
.ft-yoy-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 2px 14px; }
.ft-yoy-more { width: 100%; margin-top: 8px; padding: 10px; border: none; border-radius: 10px; font-family: inherit; font-size: 13px; color: var(--ft-ink-2); background: none; cursor: pointer; transition: background .2s var(--ft-ease); }
.ft-yoy-more:hover { background: var(--ft-surface); }
@media (max-width: 760px) { .ft-yoy-grid { grid-template-columns: 1fr; } }
`;

// Small text chip with a colored status dot.
const StatusChip = ({ type }) => {
  const meta = STATUS_META[type];
  if (!meta) return null;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        background: meta.soft, color: meta.ink,
        borderRadius: 980, padding: '2px 8px',
        fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: meta.ink, flexShrink: 0 }} />
      {meta.label}
    </span>
  );
};

// League avatar — avatarUrl may already be a full URL; use as-is when it is.
const LeagueAvatar = ({ league, size = 34 }) => {
  const src = league.avatarUrl
    ? (/^https?:\/\//.test(league.avatarUrl)
        ? league.avatarUrl
        : `https://sleepercdn.com/avatars/thumbs/${league.avatarUrl}`)
    : null;
  const radius = size < 28 ? 7 : 10;

  if (src) {
    return (
      <img
        src={src}
        alt=""
        style={{ width: size, height: size, borderRadius: radius, objectFit: 'cover', flexShrink: 0, background: 'var(--ft-surface)' }}
      />
    );
  }
  return (
    <span
      style={{
        width: size, height: size, borderRadius: radius, background: 'var(--ft-surface)', color: 'var(--ft-ink-3)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: Math.max(10, Math.round(size * 0.38)), fontWeight: 600, flexShrink: 0,
      }}
    >
      {(league.name || '?').charAt(0).toUpperCase()}
    </span>
  );
};

const DynastyYoYSection = ({ dynastyYoY, leagues = [], mergedLeagues = [], selectedLeagueId = null, sleeperUsername, dynastyYoYStatus, onDynastyYoYUpdate }) => {
  // Track expanded state for each year (must be called before any early returns)
  const [expandedYears, setExpandedYears] = useState({});
  const [isPolling, setIsPolling] = useState(false);

  // Poll for Dynasty YoY status when computing
  useEffect(() => {
    let pollInterval;

    const checkStatus = async () => {
      try {
        const response = await managerIntelAPI.getDynastyYoYStatus(sleeperUsername);

        // If completed and we were polling, refresh Dynasty YoY data
        if (response.data.status === 'complete' && isPolling) {
          setIsPolling(false);
          if (onDynastyYoYUpdate) {
            onDynastyYoYUpdate();
          }
        }
      } catch (error) {
        console.error('Failed to check Dynasty YoY status:', error);
      }
    };

    // Start polling if computing
    if (dynastyYoYStatus === 'computing' && !isPolling) {
      setIsPolling(true);
    }

    // Poll every 5 seconds if computing
    if (isPolling) {
      pollInterval = setInterval(checkStatus, 5000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [sleeperUsername, dynastyYoYStatus, isPolling, onDynastyYoYUpdate]);

  // Check if a specific league is selected (individual team timelines not ready yet)
  if (selectedLeagueId && selectedLeagueId !== 'all') {
    return (
      <div style={{ maxWidth: 560 }}>
        <span className="ft-k">Dynasty portfolio</span>
        <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--ft-ink)', margin: '10px 0 6px' }}>
          Individual team timelines coming soon.
        </div>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--ft-ink-3)' }}>
          Select "All leagues" to view the full dynasty portfolio.
        </p>
      </div>
    );
  }

  // Check if dynastyYoY is an error object (too many leagues)
  if (dynastyYoY && dynastyYoY.error === 'TOO_MANY_LEAGUES') {
    return (
      <div style={{ maxWidth: 560 }}>
        <span className="ft-k">Dynasty portfolio</span>
        <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--ft-ink)', margin: '10px 0 6px' }}>
          This manager is currently in {dynastyYoY.currentSeasonCount} dynasty leagues.
        </div>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--ft-ink-3)' }}>
          Dynasty portfolio metrics are currently available for managers with up to {dynastyYoY.maxAllowed} leagues. We're working on supporting larger accounts.
        </p>
      </div>
    );
  }

  // If computing, show loading state (even with partial data)
  if (dynastyYoYStatus === 'computing') {
    return (
      <div style={{ maxWidth: 560 }}>
        <style>{YOY_CSS}</style>
        <span className="ft-k">Dynasty portfolio</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '10px 0 8px' }}>
          <span style={{ display: 'inline-flex', gap: 4 }}>
            {[0, 1, 2].map((i) => (
              <span key={i} className="ft-yoy-pulsedot" style={{ animationDelay: `${i * 0.18}s` }} />
            ))}
          </span>
          <span style={{ fontSize: 17, fontWeight: 600, color: 'var(--ft-ink)' }}>Building the portfolio.</span>
        </div>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--ft-ink-3)' }}>
          Scans every season of league history. Takes a few minutes — the rest of the intel is ready now.
        </p>
      </div>
    );
  }

  // If no data and not computing, return null (don't show anything)
  if (!dynastyYoY || dynastyYoY.length === 0) {
    return null;
  }

  // Determine if we're in filtered view (single league selected)
  // 'all' means show all leagues, so it's not a filtered view
  const isFilteredView = selectedLeagueId !== null && selectedLeagueId !== 'all';

  // Filter to the selected league if filtering, otherwise show all
  const displayLeagues = isFilteredView
    ? mergedLeagues.filter(l => l.id === selectedLeagueId)
    : mergedLeagues;

  // Get Sleeper league IDs for the filtered league (if filtering)
  // Need to trace through ALL previous league IDs in the chain
  const filteredSleeperLeagueIds = isFilteredView && displayLeagues.length > 0
    ? (() => {
        const ids = new Set();
        displayLeagues.forEach(league => {
          // Use originalLeagueIds to find all historical versions
          const historicalDbIds = league.originalLeagueIds || [league.id];

          // Look up the Sleeper league IDs for all historical versions
          historicalDbIds.forEach(dbId => {
            const historicalLeague = leagues.find(l => l.id === dbId);
            if (historicalLeague) {
              ids.add(historicalLeague.sleeperLeagueId);
            }
          });
        });
        return Array.from(ids);
      })()
    : null;

  // Filter dynastyYoY data if filtering by specific league
  const filteredDynastyYoY = isFilteredView && filteredSleeperLeagueIds
    ? dynastyYoY.map(yearData => {
        // Check if the filtered league appears in any of the arrays for this year
        const isStartup = (yearData.startupLeagueIds || []).some(id => filteredSleeperLeagueIds.includes(id));
        const isOrphanTaken = (yearData.orphanTakenLeagueIds || []).some(id => filteredSleeperLeagueIds.includes(id));
        const isOrphanLeft = (yearData.orphanLeftLeagueIds || []).some(id => filteredSleeperLeagueIds.includes(id));
        const isFolded = (yearData.foldedLeagueIds || []).some(id => filteredSleeperLeagueIds.includes(id));
        const isActive = (yearData.activeLeagueIds || []).some(id => filteredSleeperLeagueIds.includes(id));

        return {
          ...yearData,
          newDynastyStartups: isStartup ? 1 : 0,
          orphansTakenOver: isOrphanTaken ? 1 : 0,
          orphansLeftContinued: isOrphanLeft ? 1 : 0,
          leaguesFolded: isFolded ? 1 : 0,
          totalDynastyLeagues: isActive ? 1 : 0,
        };
      })
    : dynastyYoY;

  // Use backend's leaguesToDisplay instead of complex frontend grouping
  const leaguesBySeason = {};
  dynastyYoY.forEach((yearData) => {
    const season = yearData.season;
    leaguesBySeason[season] = [];

    // For each league the backend says to display this year
    (yearData.leaguesToDisplay || []).forEach((displayInfo) => {
      // Find the league object - could be the exact ID or a rolled-over version
      let foundLeague = null;

      // First try to find in mergedLeagues (these are the latest versions)
      for (const league of mergedLeagues) {
        // Check if this league's ID matches
        if (league.sleeperLeagueId === displayInfo.leagueId) {
          foundLeague = league;
          break;
        }

        // Check if this league's chain includes the display ID (for rolled-over leagues)
        if (league.originalLeagueIds && league.originalLeagueIds.length > 0) {
          // Find the matching league in the full leagues array to check its sleeperLeagueId
          for (const originalDbId of league.originalLeagueIds) {
            const originalLeague = leagues.find(l => l.id === originalDbId);
            if (originalLeague && originalLeague.sleeperLeagueId === displayInfo.leagueId) {
              foundLeague = league; // Use the merged (latest) version
              break;
            }
          }
        }

        if (foundLeague) break;
      }

      // Fallback: check in the full leagues array
      if (!foundLeague) {
        foundLeague = leagues.find(l => l.sleeperLeagueId === displayInfo.leagueId);
      }

      if (foundLeague) {
        // Create a new object to avoid mutating the original
        const leagueWithMeta = {
          ...foundLeague,
          _joinType: displayInfo.joinType,
          _firstYear: displayInfo.firstYear,
          _userRecord: displayInfo.userRecord, // Include user's record for this season
        };
        leaguesBySeason[season].push(leagueWithMeta);
      } else if (displayInfo.metadata) {
        // For abandoned/folded leagues, use the metadata from backend
        const leagueFromMetadata = {
          id: displayInfo.leagueId,
          sleeperLeagueId: displayInfo.metadata.sleeperLeagueId,
          name: displayInfo.metadata.name,
          avatarUrl: displayInfo.metadata.avatar,
          season: season,
          _joinType: displayInfo.joinType,
          _firstYear: displayInfo.firstYear,
          _userRecord: displayInfo.userRecord || displayInfo.metadata.userRecord, // Include user's record
          settings: {}, // Empty settings object for abandoned/folded leagues
        };
        leaguesBySeason[season].push(leagueFromMetadata);
      } else {
        console.warn(`Could not find league ${displayInfo.leagueId} for season ${season} and no metadata provided`);
      }
    });
  });

  // Helper function to format record string
  const formatRecord = (record) => {
    if (!record) return null;
    const { wins, losses, ties } = record;
    if (ties > 0) {
      return `${wins}-${losses}-${ties}`;
    }
    return `${wins}-${losses}`;
  };

  // Years to display (2021-2025) - only years with leagues
  const years = ['2021', '2022', '2023', '2024', '2025', '2026'];
  const yearsWithLeagues = years.filter((year) => {
    // When filtering by specific league, only show years where that league exists
    if (isFilteredView) {
      return leaguesBySeason[year] && leaguesBySeason[year].length > 0;
    }
    // Otherwise show all years with any dynasty leagues
    const yearData = dynastyYoY.find((d) => d.season === year);
    return yearData && yearData.totalDynastyLeagues > 0;
  });

  const dataForChart = isFilteredView ? filteredDynastyYoY : dynastyYoY;

  // One row per league for the history timeline, built from the join/leave
  // events the backend emits per season (leaguesToDisplay).
  const leagueSpans = (() => {
    const spans = new Map();
    years.forEach((year) => {
      (leaguesBySeason[year] || []).forEach((league) => {
        const key = league.sleeperLeagueId || league.id || league.name;
        const type = league._joinType;
        if (!spans.has(key)) {
          const firstYear = String(league._firstYear || year);
          spans.set(key, {
            key,
            league,
            startYear: years.includes(firstYear) ? firstYear : year,
            joinType: type === 'startup' || type === 'orphan' || type === 'established' ? type : null,
            endYear: null,
            endType: null,
          });
        }
        const span = spans.get(key);
        if (type === 'left' || type === 'folded') {
          span.endYear = year;
          span.endType = type;
        }
      });
    });
    return Array.from(spans.values()).sort((a, b) => parseInt(a.startYear) - parseInt(b.startYear));
  })();

  const lastChartYear = yearsWithLeagues[yearsWithLeagues.length - 1];
  const portfolioSize = dataForChart.find((d) => d.season === lastChartYear)?.totalDynastyLeagues || 0;

  return (
    <div style={{ maxWidth: '100%' }}>
      <style>{YOY_CSS}</style>

      {/* League history — one row per league, year columns, portfolio count row */}
      {!isFilteredView && (
        <section className="ft-card" style={{ padding: '20px 20px 16px', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <span className="ft-k">League history</span>
            <span style={{ fontSize: 12, color: 'var(--ft-ink-2)' }}>
              Portfolio · <b style={{ fontWeight: 600, color: 'var(--ft-ink)' }}>{portfolioSize}</b> {portfolioSize === 1 ? 'league' : 'leagues'}
            </span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `minmax(92px, 150px) repeat(${yearsWithLeagues.length}, 1fr)`,
              alignItems: 'center',
              gap: '12px 8px',
            }}
          >
            {yearsWithLeagues.map((year, i) => {
              const count = dataForChart.find((d) => d.season === year)?.totalDynastyLeagues || 0;
              return (
                <span key={`hd-${year}`} style={{ gridRow: 1, gridColumn: i + 2, textAlign: 'center' }}>
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ft-ink-3)' }}>{year}</span>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 650, color: 'var(--ft-ink)', fontVariantNumeric: 'tabular-nums', marginTop: 1 }}>
                    {count}
                  </span>
                </span>
              );
            })}
            <span
              style={{
                gridRow: 1, gridColumn: 1, alignSelf: 'end',
                fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ft-ink-3)',
              }}
            >
              Leagues
            </span>

            {leagueSpans.map((span, rowIdx) => {
              const startIdx = Math.max(yearsWithLeagues.indexOf(span.startYear), 0);
              const endIdx = span.endYear
                ? Math.max(yearsWithLeagues.indexOf(span.endYear), startIdx)
                : yearsWithLeagues.length - 1;
              const joinMeta = span.joinType ? STATUS_META[span.joinType] : null;
              const endMeta = span.endType ? STATUS_META[span.endType] : null;
              const row = rowIdx + 2;
              return (
                <React.Fragment key={span.key}>
                  <span style={{ gridRow: row, gridColumn: 1, display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <LeagueAvatar league={span.league} size={22} />
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ft-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {span.league.name}
                    </span>
                  </span>
                  <div
                    style={{
                      gridRow: row,
                      gridColumn: `${startIdx + 2} / ${endIdx + 3}`,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      height: 18,
                      padding: '0 9px',
                      borderRadius: 980,
                      overflow: 'hidden',
                      background: endMeta ? 'var(--ft-surface-hover, #EBEBED)' : (joinMeta || STATUS_META.startup).soft,
                    }}
                  >
                    {/* Status as text, not just color — readable for everyone */}
                    {joinMeta && joinMeta.short && (
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: joinMeta.ink, whiteSpace: 'nowrap' }}>
                        {joinMeta.short}
                      </span>
                    )}
                    {endMeta && (
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: endMeta.ink, whiteSpace: 'nowrap', marginLeft: 'auto' }}>
                        {endMeta.short}
                      </span>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

          </div>

          {/* Legend */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 18px', marginTop: 16 }}>
            {['startup', 'orphan', 'established', 'left', 'folded'].map((type) => (
              <span key={type} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--ft-ink-3)' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: STATUS_META[type].ink, flexShrink: 0 }} />
                {STATUS_META[type].label}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Single League Timeline - only show when filtering by one league */}
      {isFilteredView && displayLeagues.length === 1 && (
        <section className="ft-card white" style={{ padding: 22, marginBottom: 24 }}>
          <span className="ft-k">League timeline</span>
          <div style={{ fontSize: 12.5, color: 'var(--ft-ink-3)', margin: '4px 0 18px' }}>
            Your history in {displayLeagues[0].name}
          </div>

          {/* Timeline */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {yearsWithLeagues.map((year, index) => {
              const yearData = filteredDynastyYoY.find(d => d.season === year);

              // Check if league appears in various arrays for this year
              const isStartup = (yearData?.startupLeagueIds || []).some(id => filteredSleeperLeagueIds.includes(id));
              const isOrphanTaken = (yearData?.orphanTakenLeagueIds || []).some(id => filteredSleeperLeagueIds.includes(id));
              const isOrphanLeft = (yearData?.orphanLeftLeagueIds || []).some(id => filteredSleeperLeagueIds.includes(id));
              const isFolded = (yearData?.foldedLeagueIds || []).some(id => filteredSleeperLeagueIds.includes(id));
              const isActive = (yearData?.activeLeagueIds || []).some(id => filteredSleeperLeagueIds.includes(id));
              const isFirst = index === yearsWithLeagues.length - 1;
              const isLast = index === 0;

              let event = '';
              let eventInk = 'var(--ft-ink-3)';
              if (isFirst && isStartup) {
                event = 'Started new dynasty league';
                eventInk = STATUS_META.startup.ink;
              } else if (isFirst && isOrphanTaken) {
                event = 'Took over orphaned team';
                eventInk = STATUS_META.orphan.ink;
              } else if (isOrphanLeft) {
                event = 'Left league (continued without you)';
                eventInk = STATUS_META.left.ink;
              } else if (isFolded) {
                event = 'League folded';
                eventInk = STATUS_META.folded.ink;
              } else if (isActive) {
                event = 'Active season';
                eventInk = 'var(--ft-green-ink)';
              }

              return (
                <div
                  key={year}
                  style={{ display: 'flex', gap: 14, position: 'relative', paddingBottom: isLast ? 0 : 22 }}
                >
                  {/* Hairline connector */}
                  {!isLast && (
                    <div style={{ position: 'absolute', left: 3.5, top: 16, bottom: 0, width: 1, background: 'var(--ft-hairline)' }} />
                  )}

                  {/* Status dot */}
                  <span
                    style={{
                      width: 8, height: 8, borderRadius: '50%', background: eventInk,
                      flexShrink: 0, marginTop: 5, position: 'relative', zIndex: 1,
                    }}
                  />

                  {/* Year + event */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="ft-k" style={{ display: 'block', marginBottom: 2 }}>{year}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ft-ink)' }}>{event}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Year-by-year timeline - hide when viewing single league */}
      {!(isFilteredView && displayLeagues.length === 1) && (
        <div>
          <span className="ft-k" style={{ display: 'block', marginBottom: 12 }}>Year by year</span>
          {yearsWithLeagues
            .slice()
            .reverse()
            .map((season) => {
              const yearData = dataForChart.find((d) => d.season === season);
              const seasonLeagues = leaguesBySeason[season] || [];
              const expanded = expandedYears[season] || false;

              // When filtering, show years where we have leagues even if totalDynastyLeagues is 0
              // This handles cases where a league might be in startup/orphan arrays but not active array
              if (!yearData) {
                return null;
              }

              if (!isFilteredView && yearData.totalDynastyLeagues === 0) {
                return null;
              }

              // Skip if no leagues for this year when filtering
              if (isFilteredView && seasonLeagues.length === 0) {
                return null;
              }

              const activeCount = yearData.totalDynastyLeagues;
              const leftCount = yearData.orphansLeftContinued || 0;
              const startupCount = yearData.newDynastyStartups || 0;
              const orphanCount = yearData.orphansTakenOver || 0;
              const foldedCount = yearData.leaguesFolded || 0;

              // Calculate net change (positive = growth, negative = decline)
              const netChange = startupCount + orphanCount - leftCount - foldedCount;

              // Determine tagline based on year-over-year change and activity patterns
              const getYearTagline = () => {
                const yearIndex = years.indexOf(season);
                const isFirstYear = yearIndex === 0 || (yearIndex > 0 &&
                  dataForChart.find(d => d.season === years[yearIndex - 1])?.totalDynastyLeagues === 0);

                if (isFirstYear) return 'Getting started';

                // Check for specific patterns
                const hasHighAttrition = leftCount >= 2 || foldedCount >= 2;
                const tookMultipleOrphans = orphanCount >= 2;
                const allStartups = startupCount > 0 && orphanCount === 0 && leftCount === 0;

                // High attrition patterns
                if (hasHighAttrition && netChange < 0) return 'Portfolio restructuring';
                if (hasHighAttrition && netChange >= 0) return 'High turnover year';

                // Orphan takeover patterns
                if (tookMultipleOrphans && netChange >= 2) return 'Aggressive orphan acquisition';
                if (tookMultipleOrphans) return 'Expansion via orphans';

                // Clean growth patterns
                if (allStartups && netChange >= 2) return 'New league expansion';
                if (allStartups) return 'Steady growth';

                // General growth/decline
                if (netChange >= 3) return 'Major portfolio shift';
                if (netChange === 2) return 'Rapid expansion';
                if (netChange === 1) return 'Portfolio growth';
                if (netChange === 0 && leftCount === 0) return 'Holding steady';
                if (netChange === 0) return 'Stable year';
                if (netChange === -1) return 'Slight contraction';
                return 'Scaling back';
              };

              const displayedLeagues = expanded ? seasonLeagues : seasonLeagues.slice(0, 6);
              const remainingCount = seasonLeagues.length - 6;

              // Helper to determine join type for a league
              // Now super simple - just use the backend data!
              const getLeagueJoinType = (league) => {
                // Backend attached this when building leaguesBySeason
                if (league._joinType) {
                  return league._joinType;
                }

                // Fallback to unknown
                return 'unknown';
              };

              // Helper to get first joined season (find earliest year this league appears)
              const getFirstJoinedSeason = (league) => {
                // Backend attached this when building leaguesBySeason
                if (league._firstYear) {
                  return league._firstYear;
                }

                // Fallback to current season
                return season;
              };

              const getYearsSince = (firstJoinedSeason) => {
                if (!firstJoinedSeason) return 'unknown tenure';
                const currentYear = 2026;
                const yearsCount = currentYear - parseInt(firstJoinedSeason);
                if (yearsCount === 0) return 'this year';
                if (yearsCount === 1) return '1 year';
                return `${yearsCount} years`;
              };

              // Quiet one-line summary - only non-zero items, status colors on the numbers
              const summaryParts = [
                activeCount > 0 && { n: activeCount, label: 'active', ink: 'var(--ft-ink)' },
                startupCount > 0 && { n: startupCount, label: startupCount === 1 ? 'startup' : 'startups', ink: STATUS_META.startup.ink },
                orphanCount > 0 && { n: orphanCount, label: 'adopted', ink: STATUS_META.orphan.ink },
                leftCount > 0 && { n: leftCount, label: 'abandoned', ink: STATUS_META.left.ink },
                foldedCount > 0 && { n: foldedCount, label: 'folded', ink: STATUS_META.folded.ink },
              ].filter(Boolean);

              return (
                <section key={season} className="ft-card white" style={{ padding: '18px 20px', marginBottom: 12 }}>
                  {/* Header row: year + tagline */}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--ft-ink)' }}>{season}</span>
                    <span style={{ fontSize: 13, color: 'var(--ft-ink-3)' }}>· {getYearTagline()}</span>
                  </div>

                  {/* Counts summary */}
                  {summaryParts.length > 0 && (
                    <div style={{ fontSize: 12.5, color: 'var(--ft-ink-3)', marginTop: 5 }}>
                      {summaryParts.map((p, i) => (
                        <span key={p.label}>
                          {i > 0 && <span style={{ margin: '0 6px' }}>·</span>}
                          <b style={{ fontWeight: 600, color: p.ink }}>{p.n}</b> {p.label}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* League rows - only show if we have league data for this season */}
                  {seasonLeagues.length > 0 && (
                    <>
                      <div className="ft-yoy-grid" style={{ marginTop: 14 }}>
                        {displayedLeagues.map((league) => {
                          const joinType = getLeagueJoinType(league);
                          const firstJoinedSeason = getFirstJoinedSeason(league);
                          const recordString = formatRecord(league._userRecord);
                          const showTenure = joinType !== 'left' && joinType !== 'folded';

                          return (
                            <div key={league.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '7px 4px', minWidth: 0 }}>
                              <LeagueAvatar league={league} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ft-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {league.name}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginTop: 3 }}>
                                  <StatusChip type={joinType} />
                                  {recordString && (
                                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ft-ink-2)' }}>{recordString}</span>
                                  )}
                                  {showTenure && (
                                    <span style={{ fontSize: 12, color: 'var(--ft-ink-3)' }}>{getYearsSince(firstJoinedSeason)}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Show More Button */}
                      {!expanded && remainingCount > 0 && (
                        <button
                          type="button"
                          className="ft-yoy-more"
                          onClick={() => setExpandedYears({ ...expandedYears, [season]: true })}
                        >
                          Show {remainingCount} more {remainingCount === 1 ? 'league' : 'leagues'}
                        </button>
                      )}
                    </>
                  )}
                </section>
              );
            })}
        </div>
      )}

      {/* Portfolio Note */}
      <p style={{ fontSize: 12, color: 'var(--ft-ink-3)', margin: '20px 0 0' }}>
        Portfolio covers 2021 through the current season.
      </p>
    </div>
  );
};

export default DynastyYoYSection;
